import { DataTable } from "@/components/data-table";
import { Tabs, TabsContent, TabsList } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { columns } from "../_misc/columns";
import {
  useGetCardTransactions,
  useGetDeposits,
  useHasSwaps,
} from "@/hooks/use-queries";
import TopBar from "@/components/topbar";
import { cardColumns } from "../_misc/cardColumns";
import TransactionList from "../_misc/TransactionList";
import SwapTab from "../_misc/swap/swap-tab";
import PillTabsTrigger from "@/components/pill-tabs-trigger";
import { useTabParam } from "@/hooks/use-tab-param";

const PAGE_SIZE = 10;

type TransactionTab = "wallet" | "cards" | "swap";

const Transactions = () => {
  const hasSwaps = useHasSwaps();
  const [tab, setTab] = useTabParam<TransactionTab>(
    "tab",
    "wallet",
    hasSwaps ? ["wallet", "cards", "swap"] : ["wallet", "cards"],
  );

  // ── Wallet: server-paginated ────────────────────────────────────────────
  const [walletPage, setWalletPage] = useState(1);
  const { data: transactions, isLoading: isLoadingTransactions } =
    useGetDeposits({ page: walletPage, limit: PAGE_SIZE });
  const walletRows: Transaction[] = transactions?.data ?? [];
  const walletPageCount = Math.max(1, transactions?.totalPages || 1);

  // ── Cards: fetched in one batch, paginated on the client ────────────────
  const [cardPage, setCardPage] = useState(1);
  const { data: cardTransactions, isLoading: isLoadingCardTransactions } =
    useGetCardTransactions({ page: 0, limit: 150 });
  const cardRows: CardTransaction[] = cardTransactions?.data ?? [];
  const cardPageCount = Math.max(1, Math.ceil(cardRows.length / PAGE_SIZE));
  const cardPageRows = cardRows.slice(
    (cardPage - 1) * PAGE_SIZE,
    cardPage * PAGE_SIZE,
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Keep the active page in range if the row count shrinks under it. Guard on a
  // loaded response — while react-query refetches an uncached page, `data` is
  // briefly undefined and `totalPages` would read as 1, bouncing us to page 1.
  useEffect(() => {
    if (transactions?.totalPages) {
      setWalletPage((p) => Math.min(p, walletPageCount));
    }
  }, [transactions?.totalPages, walletPageCount]);
  useEffect(() => {
    setCardPage((p) => Math.min(p, cardPageCount));
  }, [cardPageCount]);

  // <DataTable> wants a 0-indexed { pageIndex, pageSize } object and a tanstack
  // setter; bridge it to a plain 1-indexed page number, clamping whatever
  // tanstack's nav hands back into range.
  const tableProps = (
    page: number,
    setPage: (p: number) => void,
    pageCount: number,
  ) => ({
    pageCount,
    pagination: { pageIndex: page - 1, pageSize: PAGE_SIZE },
    setPagination: (updater: unknown) => {
      const prev = { pageIndex: page - 1, pageSize: PAGE_SIZE };
      const next =
        typeof updater === "function"
          ? (updater as (p: typeof prev) => typeof prev)(prev)
          : (updater as typeof prev);
      const target = (next?.pageIndex ?? page - 1) + 1;
      setPage(Math.min(Math.max(1, target), Math.max(1, pageCount)));
    },
  });

  const pageChanger =
    (setPage: (p: number) => void, pageCount: number) => (p: number) => {
      if (p >= 1 && p <= pageCount) setPage(p);
    };

  return (
    <div className="">
      <TopBar title="Transactions" />
      <Tabs
        onValueChange={(value) => setTab(value as TransactionTab)}
        value={tab}
        className="font-poppins px-4 py-4 lg:bg-transparent"
      >
        <TabsList className="border-dark-stroke-3 h-10 w-fit rounded-full border bg-white/5 p-1">
          <PillTabsTrigger value="wallet" active={tab === "wallet"}>
            Wallet
          </PillTabsTrigger>
          <PillTabsTrigger value="cards" active={tab === "cards"}>
            Cards
          </PillTabsTrigger>
          {hasSwaps && (
            <PillTabsTrigger value="swap" active={tab === "swap"}>
              Swap
            </PillTabsTrigger>
          )}
        </TabsList>
        <div className="bg-dark-card-3 text-white mt-4 rounded-2xl p-4 backdrop-blur-sm lg:row-start-auto">
          <div className="flex items-center justify-between bg-white/5"></div>

          <TabsContent value="wallet" className="mt-2">
            <div className="hidden lg:block">
              <DataTable
                columns={columns}
                data={walletRows}
                isLoading={isLoadingTransactions}
                {...tableProps(walletPage, setWalletPage, walletPageCount)}
              />
            </div>
            <div className="lg:hidden">
              <TransactionList
                isLoading={isLoadingTransactions}
                transactions={walletRows}
                currentPage={walletPage}
                totalPages={walletPageCount}
                handlePageChange={pageChanger(setWalletPage, walletPageCount)}
              />
            </div>
          </TabsContent>

          <TabsContent value="cards" className="mt-2">
            <div className="hidden lg:block">
              <DataTable
                columns={cardColumns}
                data={cardPageRows}
                isLoading={isLoadingCardTransactions}
                {...tableProps(cardPage, setCardPage, cardPageCount)}
              />
            </div>
            <div className="lg:hidden">
              <TransactionList
                isLoading={isLoadingCardTransactions}
                transactions={cardPageRows}
                type="cards"
                currentPage={cardPage}
                totalPages={cardPageCount}
                handlePageChange={pageChanger(setCardPage, cardPageCount)}
              />
            </div>
          </TabsContent>

          {hasSwaps && (
            <TabsContent value="swap" className="mt-2">
              <SwapTab />
            </TabsContent>
          )}
        </div>
      </Tabs>
    </div>
  );
};

export default Transactions;
